import { MATERIAL_UNITS, MAX_REQUISITION_LINES } from '@felix/domain';
import type { Material } from '../materials/types.js';

/**
 * Talebe toplu satır (ortağın notu #6; ADR 0047 §3): Excel'den yapıştırılan satırları okuma,
 * katalogla eşleme ve 200 satır sınırı. Saf yardımcılar; eşleşmeyen satırı serbest kaleme
 * çevirme kararı kullanıcınındır.
 */

/** Yapıştırmada okunan en çok satır: talebin satır sınırı. */
export const PASTE_MAX_ROWS = MAX_REQUISITION_LINES;

export interface PastedRow {
  /** Yapıştırılan satırın sırası (1'den). */
  line: number;
  /** Kod ya da ad (ilk sütun). */
  code: string;
  /** Miktar; okunamadıysa `null`. */
  quantity: number | null;
  /** Tanınan birim; yoksa ya da tanınmadıysa `null`. */
  unit: string | null;
  /** Yapıştırılan birim metni (not için). */
  unitText: string;
}

const SYNONYMS: Readonly<Record<string, string>> = {
  PC: 'PCS',
  PCS: 'PCS',
  PIECE: 'PCS',
  PIECES: 'PCS',
  EA: 'PCS',
  EACH: 'PCS',
  ADET: 'PCS',
  M: 'MTR',
  MT: 'MTR',
  MTR: 'MTR',
  METER: 'MTR',
  METRE: 'MTR',
  METERS: 'MTR',
  METRES: 'MTR',
  KG: 'KG',
  KGS: 'KG',
  KILO: 'KG',
  L: 'LTR',
  LT: 'LTR',
  LTR: 'LTR',
  LITER: 'LTR',
  LITRE: 'LTR',
  LITERS: 'LTR',
  LITRES: 'LTR',
  SET: 'SET',
  SETS: 'SET',
  TIN: 'TIN',
  TINS: 'TIN',
  CAN: 'TIN',
  CANS: 'TIN',
};

/** Yapıştırılan birim → katalog birimi (`MATERIAL_UNITS`); tanınmazsa `null`. */
export function normalizeUnit(text: string): string | null {
  const key = text.trim().toUpperCase().replace(/\.$/, '');
  const unit = SYNONYMS[key] ?? null;
  return unit && (MATERIAL_UNITS as readonly string[]).includes(unit) ? unit : null;
}

/** Miktar: ondalık virgül ve boşluklu binlik kabul; sıfır ve eksi geçersiz. */
function quantityOf(text: string, delimiter: string): number | null {
  let value = text.trim().replace(/\s/g, '');
  if (delimiter !== ',') value = value.replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(value)) return null;
  const number = Number(value);
  return number > 0 ? number : null;
}

const delimiterOf = (line: string) => (line.includes('\t') ? '\t' : line.includes(';') ? ';' : ',');

/**
 * Excel, Google Sheets ya da CSV'den yapıştırılan satırlar: sütunlar kod/ad, miktar, birim.
 * Ayırıcı satır satır: sekme, yoksa noktalı virgül, yoksa virgül. Boş satır atlanır; miktarı sayı
 * olmayan ilk satır başlık sayılır. En çok `PASTE_MAX_ROWS` satır okunur.
 */
export function parsePastedLines(text: string): {
  rows: PastedRow[];
  skippedHeader: boolean;
  truncated: boolean;
} {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');
  const rows: PastedRow[] = [];
  let skippedHeader = false;
  let truncated = false;
  for (const [index, line] of lines.entries()) {
    const delimiter = delimiterOf(line);
    const cells = line.split(delimiter).map((cell) => cell.trim());
    const code = (cells[0] ?? '').replace(/\s+/g, ' ');
    const quantity = quantityOf(cells[1] ?? '', delimiter);
    if (index === 0 && quantity === null) {
      skippedHeader = true;
      continue;
    }
    if (!code) continue;
    if (rows.length >= PASTE_MAX_ROWS) {
      truncated = true;
      break;
    }
    const unitText = cells[2] ?? '';
    rows.push({ line: index + 1, code, quantity, unit: normalizeUnit(unitText), unitText });
  }
  return { rows, skippedHeader, truncated };
}

const key = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Yapıştırılan kod/ad ile katalog kalemi: şirket kodu, IMPA ya da ISSA tam eşleşmesi; yoksa ad tek
 * kalemle eşleşirse o kalem. Belirsiz ya da eşleşmeyen satır `null` (serbest kalem önerisi).
 */
export function catalogMatch(code: string, candidates: readonly Material[]): Material | null {
  const wanted = key(code);
  if (!wanted) return null;
  const byCode = candidates.filter((m) =>
    [m.companyCode, m.impaCode, m.issaCode].some((c) => c !== null && key(c) === wanted),
  );
  if (byCode.length === 1) return byCode[0] ?? null;
  if (byCode.length > 1) return null;
  const byName = candidates.filter((m) => key(m.name) === wanted);
  return byName.length === 1 ? (byName[0] ?? null) : null;
}

/** Talebe eklenebilecek satır sayısı (`MAX_REQUISITION_LINES`). */
export function remainingLines(existing: number): number {
  return Math.max(0, MAX_REQUISITION_LINES - existing);
}
